import * as z from "zod";
import {
  AnalyticsRelationshipMeaningNameSchema,
  isPrimaryAnalyticsRelationshipMeaning,
  type AnalyticsRelationshipMeaningName,
} from "../../../semantic-catalog/v1/relationships/analytics-relationship-route-pins";

export const AnalyticsNamespace = {
  CheckoutVisits: "checkout_visits",
  CheckoutVisitsAdvanced: "checkout_visit_advanced",
  CheckoutVisitTreatments: "checkout_visit_treatments",
  Treatments: "treatments",
  Splits: "splits",
  OrderShippingLines: "order_shipping_lines",
  OrderRefundLineItems: "order_refund_line_items",
  CheckoutWidgets: "checkout_widgets",
  CheckoutWidgetAttributedOrderLineItems:
    "checkout_widget_attributed_order_line_items",
  OrderLineItemProperties: "order_line_item_properties",
  OrderFulfillments: "order_fulfillments",
  Bills: "bills",
  BillingCharges: "billing_charges",
  OrderFulfillmentLineItems: "order_fulfillment_line_items",
  ReturnShipmentTrackingDetails: "return_shipment_tracking_details",
  ReturnLineItemMultipleChoice: "return_line_item_multiple_choice",
  FulfillmentGroups: "fulfillment_groups",
  FulfillmentGroupLineItems: "fulfillment_group_line_items",
  FulfillmentGroupShipments: "fulfillment_group_shipments",
  FulfillmentGroupShipmentSurcharges: "fulfillment_group_shipment_surcharges",
  Orders: "orders",
  OrderLineItems: "order_line_items",
  ExchangeOrders: "exchange_orders",
  ReturnLineItems: "return_line_items",
  Returns: "returns",
  ReturnPaymentIntents: "return_payment_intents",
  ReturnShipments: "return_shipments",
  MarketingOutreachEvents: "marketing_outreach_events",
  Campaigns: "campaigns",
  Automations: "automations",
  PostPurchaseUpsellEvents: "post_purchase_upsell_events",
  PostPurchaseUpsellPages: "post_purchase_upsell_pages",
  Products: "products",
  InventoryLevels: "inventory_levels",
  StorageLocations: "storage_locations",
  Locations: "locations",
  RescuePanelMetrics: "rescue_panel_metrics",
  RescuePanelAttributedOrders: "rescue_panel_attributed_orders",
  ShippingClaims: "shipping_claims",
  DraftReturnPendingReturnItems: "draft_return_pending_return_items",
  ReturnTags: "return_tags",
  SatisfactionResponses: "satisfaction_responses",
  OrderExtendedWarranties: "order_extended_warranties",
  Conversations: "conversations",
  ConversationMessages: "conversation_messages",
  FailedDiscountRescueEvents: "failed_discount_rescue_events",
  CustomerTags: "customer_tags",
  CustomCustomerEvents: "custom_customer_events",
  CustomerCustomFields: "customer_custom_fields",
  ProductTags: "product_tags",
  FulfillmentGroupTrackingEvents: "fulfillment_group_tracking_events",
  ProductCollections: "product_collections",
  ProductReviews: "product_reviews",
  DailyShopperSessions: "daily_shopper_sessions",
  MarketingAgentDecisions: "marketing_agent_decisions",
  MarketingAgentDecisionTags: "marketing_agent_decision_tags",
  CustomerGroupMemberships: "customer_group_memberships",
  CampaignRecipients: "campaign_recipients",
  CheckoutDeliveryOptions: "checkout_delivery_options",
  CheckoutDeliveryGroups: "checkout_delivery_groups",
  CheckoutInfoChanges: "checkout_info_changes",
  CheckoutShippingStepEvents: "checkout_shipping_step_events",
  CheckoutOrderCreatedEvents: "checkout_order_created_events",
  CheckoutWidgetInteractions: "checkout_widget_interactions",
  PostPurchaseSplitAssignments: "post_purchase_split_assignments",
  MarketingSubscriptionEvents: "marketing_subscription_events",
  BackInStockEvents: "back_in_stock_events",
  CollectionViewEvents: "collection_view_events",
  ProductViewEvents: "product_view_events",
  StorefrontCheckoutEvents: "storefront_checkout_events",
  CartChangeEvents: "cart_change_events",
  TryFirstPlans: "try_first_plans",
  TryFirstContracts: "try_first_contracts",
  ReturnAssessmentResponses: "return_assessment_responses",
  ReturnAssessmentDefinitionFields: "return_assessment_definition_fields",
  ReturnAssessments: "return_assessments",
  TrackingPageEvents: "tracking_page_events",
  OrderTrackingPageOfferClicks: "order_tracking_page_offer_clicks",
  OrderTrackingEmailEvents: "order_tracking_email_events",
  OrderTrackingDetails: "order_tracking_details",
  InboundMessages: "inbound_messages",
  MercuryShippingLines: "mercury_shipping_lines",
  OrderPaymentGatewayNames: "order_payment_gateway_names",
  Integrations: "integrations",
  Customers: "customers",
  SupportTickets: "support_tickets",
  SupportTicketMessages: "support_ticket_messages",
  MessageTopicLinks: "message_topic_links",
  SupportTicketTopics: "support_ticket_topics",
} as const;

export type AnalyticsNamespace =
  (typeof AnalyticsNamespace)[keyof typeof AnalyticsNamespace];

export interface AnalyticsNamespacedRef<
  TNamespace extends AnalyticsNamespace = AnalyticsNamespace,
  TValue extends string = string,
> {
  readonly namespace: TNamespace;
  readonly value: TValue;
}

export type AnalyticsRef = AnalyticsNamespacedRef;

export type AnalyticsRefKey = `${AnalyticsNamespace}.${string}`;

const AnalyticsRefValueSyntaxRegex = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/;

export const AnalyticsNamespaceSchema = z.enum(AnalyticsNamespace);

export const AnalyticsRefValueSchema = z
  .string()
  .regex(AnalyticsRefValueSyntaxRegex)
  .max(120);

export const AnalyticsRefKeySchema: z.ZodType<AnalyticsRefKey> =
  z.custom<AnalyticsRefKey>(
    (value) =>
      typeof value === "string" &&
      value.length <= 160 &&
      Object.values(AnalyticsNamespace).some((namespace) =>
        value.startsWith(`${namespace}.`),
      ) &&
      AnalyticsRefValueSyntaxRegex.test(value.split(".").slice(1).join(".")),
    { message: "Invalid analytics ref key." },
  );

export const AnalyticsRefSyntaxSchema: z.ZodType<AnalyticsRef> = z.strictObject(
  { namespace: AnalyticsNamespaceSchema, value: AnalyticsRefValueSchema },
);

/** A ref that may name a declared non-primary route. Absence means the primary route. */
export type AnalyticsRoutedRef<TRef = AnalyticsRef> = TRef & {
  readonly meaningName?: AnalyticsRelationshipMeaningName;
};

const AnalyticsNonPrimaryMeaningNameSchema =
  AnalyticsRelationshipMeaningNameSchema.refine(
    (meaningName) => !isPrimaryAnalyticsRelationshipMeaning(meaningName),
    { message: "The primary route is named by omitting the meaningName." },
  );

export const AnalyticsRoutedRefSchema: z.ZodType<AnalyticsRoutedRef> =
  z.strictObject({
    namespace: AnalyticsNamespaceSchema,
    value: AnalyticsRefValueSchema,
    meaningName: AnalyticsNonPrimaryMeaningNameSchema.optional(),
  });

export type AnalyticsSelectionKey =
  | AnalyticsRefKey
  | `${AnalyticsRefKey}@${string}`;

/**
 * COLUMN identity: two columns on one catalog entry with different roles are two
 * columns. Catalog identity stays `analyticsRefKey`, which ignores the meaningName.
 */
export function analyticsSelectionKey(
  ref: AnalyticsRoutedRef,
): AnalyticsSelectionKey {
  return ref.meaningName
    ? `${analyticsRefKey(ref)}@${ref.meaningName}`
    : analyticsRefKey(ref);
}

export function analyticsRef<
  const TNamespace extends AnalyticsNamespace,
  const TValue extends string,
>(
  namespace: TNamespace,
  value: TValue,
): AnalyticsNamespacedRef<TNamespace, TValue> {
  return { namespace, value };
}

export function analyticsRefKey(ref: AnalyticsRef): AnalyticsRefKey {
  return AnalyticsRefKeySchema.parse(`${ref.namespace}.${ref.value}`);
}

export function analyticsRefsEqual(left: AnalyticsRef, right: AnalyticsRef) {
  return left.namespace === right.namespace && left.value === right.value;
}

export const AnalyticsMetricOutputRefType = "metric_output";

export type AnalyticsMetricOutputId<TValue extends string = string> = TValue;

export interface AnalyticsMetricOutputRef<TValue extends string = string> {
  readonly type: typeof AnalyticsMetricOutputRefType;
  readonly value: TValue;
}

export type AnalyticsMetricOutputRefKey =
  `${typeof AnalyticsMetricOutputRefType}.${string}`;

export type AnalyticsOutputRef =
  | AnalyticsRef
  | AnalyticsMetricOutputRef
  | AnalyticsDimensionOutputRef;

export const AnalyticsDimensionOutputRefType = "dimension_output";

export type AnalyticsDimensionOutputId<TValue extends string = string> = TValue;

export interface AnalyticsDimensionOutputRef<TValue extends string = string> {
  readonly type: typeof AnalyticsDimensionOutputRefType;
  readonly value: TValue;
}

export type AnalyticsDimensionOutputRefKey =
  `${typeof AnalyticsDimensionOutputRefType}.${string}`;

export type AnalyticsOutputRefKey =
  | AnalyticsRefKey
  | AnalyticsMetricOutputRefKey
  | AnalyticsDimensionOutputRefKey;

export const AnalyticsMetricOutputIdSchema: z.ZodType<AnalyticsMetricOutputId> =
  AnalyticsRefValueSchema;

export const AnalyticsMetricOutputRefSchema: z.ZodType<AnalyticsMetricOutputRef> =
  z.strictObject({
    type: z.literal(AnalyticsMetricOutputRefType),
    value: AnalyticsRefValueSchema,
  });

export function analyticsMetricOutputId<const TValue extends string>(
  value: TValue,
): AnalyticsMetricOutputId<TValue> {
  return value;
}

export function analyticsMetricOutputRef<const TValue extends string>(
  value: TValue,
): AnalyticsMetricOutputRef<TValue> {
  return { type: AnalyticsMetricOutputRefType, value };
}

export function isAnalyticsMetricOutputRef(
  ref: AnalyticsOutputRef,
): ref is AnalyticsMetricOutputRef {
  return "type" in ref && ref.type === AnalyticsMetricOutputRefType;
}

export function analyticsMetricOutputRefKey(
  ref: AnalyticsMetricOutputRef,
): AnalyticsMetricOutputRefKey {
  return `${ref.type}.${ref.value}`;
}

export function analyticsMetricOutputIdKey<const TValue extends string>(
  id: AnalyticsMetricOutputId<TValue>,
): `${typeof AnalyticsMetricOutputRefType}.${TValue}` {
  return `${AnalyticsMetricOutputRefType}.${id}`;
}

export const AnalyticsDimensionOutputIdSchema: z.ZodType<AnalyticsDimensionOutputId> =
  AnalyticsRefValueSchema;

export const AnalyticsDimensionOutputRefSchema: z.ZodType<AnalyticsDimensionOutputRef> =
  z.strictObject({
    type: z.literal(AnalyticsDimensionOutputRefType),
    value: AnalyticsRefValueSchema,
  });

export function analyticsDimensionOutputRef<const TValue extends string>(
  value: TValue,
): AnalyticsDimensionOutputRef<TValue> {
  return { type: AnalyticsDimensionOutputRefType, value };
}

export function isAnalyticsDimensionOutputRef(
  ref: AnalyticsOutputRef,
): ref is AnalyticsDimensionOutputRef {
  return "type" in ref && ref.type === AnalyticsDimensionOutputRefType;
}

export function analyticsDimensionOutputRefKey(
  ref: AnalyticsDimensionOutputRef,
): AnalyticsDimensionOutputRefKey {
  return `${ref.type}.${ref.value}`;
}

export function analyticsDimensionOutputIdKey<const TValue extends string>(
  id: AnalyticsDimensionOutputId<TValue>,
): `${typeof AnalyticsDimensionOutputRefType}.${TValue}` {
  return `${AnalyticsDimensionOutputRefType}.${id}`;
}

export type AnalyticsExpressionRef = AnalyticsOutputRef;

export type AnalyticsExpressionRefKey = AnalyticsOutputRefKey;

export type AnalyticsColumnOutputRef =
  | AnalyticsMetricOutputRef
  | AnalyticsDimensionOutputRef;

export const AnalyticsColumnOutputRefSchema: z.ZodType<AnalyticsColumnOutputRef> =
  z.union([AnalyticsMetricOutputRefSchema, AnalyticsDimensionOutputRefSchema]);

export function analyticsOutputRefKey(
  ref: AnalyticsOutputRef,
): AnalyticsOutputRefKey;
export function analyticsOutputRefKey(
  ref: AnalyticsExpressionRef,
): AnalyticsExpressionRefKey;
export function analyticsOutputRefKey(
  ref: AnalyticsExpressionRef,
): AnalyticsExpressionRefKey {
  if (isAnalyticsMetricOutputRef(ref)) {
    return analyticsMetricOutputRefKey(ref);
  }
  return isAnalyticsDimensionOutputRef(ref)
    ? analyticsDimensionOutputRefKey(ref)
    : analyticsRefKey(ref);
}

/** An output ref position that also accepts a route meaningName on its catalog-ref arm. */
export type AnalyticsRoutedOutputRef =
  | AnalyticsRoutedRef
  | AnalyticsMetricOutputRef
  | AnalyticsDimensionOutputRef;

export function analyticsOutputSelectionKey(
  ref: AnalyticsRoutedOutputRef,
): AnalyticsOutputRefKey | AnalyticsSelectionKey {
  if (isAnalyticsMetricOutputRef(ref)) {
    return analyticsMetricOutputRefKey(ref);
  }
  return isAnalyticsDimensionOutputRef(ref)
    ? analyticsDimensionOutputRefKey(ref)
    : analyticsSelectionKey(ref);
}

/**
 * Whether the target names a catalog ref rather than an already-selected output.
 * Only these reach the graph, so only these can carry a route meaningName.
 */
export function isAnalyticsRoutableRef(
  ref: AnalyticsRoutedOutputRef,
): ref is AnalyticsRoutedRef {
  return (
    !isAnalyticsMetricOutputRef(ref) && !isAnalyticsDimensionOutputRef(ref)
  );
}

export type AnalyticsRefEnum = Readonly<Record<string, AnalyticsRef>>;

export interface AnalyticsRefList<
  TMetricRefs extends AnalyticsRefEnum = AnalyticsRefEnum,
  TMeasureRefs extends AnalyticsRefEnum = AnalyticsRefEnum,
  TAttributeRefs extends AnalyticsRefEnum = AnalyticsRefEnum,
> {
  readonly metrics: TMetricRefs;
  readonly measures: TMeasureRefs;
  readonly attributes: TAttributeRefs;
}

export type AnalyticsMetricRef<TRefs extends AnalyticsRefList> =
  TRefs["metrics"][keyof TRefs["metrics"]];

export type AnalyticsMeasureRef<TRefs extends AnalyticsRefList> =
  TRefs["measures"][keyof TRefs["measures"]];

export type AnalyticsAttributeRef<TRefs extends AnalyticsRefList> =
  TRefs["attributes"][keyof TRefs["attributes"]];

export function defineAnalyticsRefs<
  const TMetricRefs extends AnalyticsRefEnum,
  const TMeasureRefs extends AnalyticsRefEnum,
  const TAttributeRefs extends AnalyticsRefEnum,
>(
  refs: AnalyticsRefList<TMetricRefs, TMeasureRefs, TAttributeRefs>,
): AnalyticsRefList<TMetricRefs, TMeasureRefs, TAttributeRefs> {
  const seenRefKeys = new Set<AnalyticsRefKey>();
  for (const ref of [
    ...Object.values(refs.metrics),
    ...Object.values(refs.measures),
    ...Object.values(refs.attributes),
  ]) {
    AnalyticsRefSyntaxSchema.parse(ref);
    const refKey = analyticsRefKey(ref);
    if (seenRefKeys.has(refKey)) {
      throw new Error(`Duplicate analytics ref "${refKey}".`);
    }
    seenRefKeys.add(refKey);
  }
  return refs;
}
